import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Sparkles,
  Trash2,
  ExternalLink,
  Plus,
  BookOpen,
  Calendar,
  User,
  Search,
  AlertTriangle,
  CheckCircle2,
  Eye,
  FileCode,
  Edit3,
  Layers,
  Filter,
  RotateCcw,
  Clock,
  HelpCircle,
  RefreshCw,
  Check,
} from 'lucide-react'
import AdminNav from '../components/AdminNav'
import { useAuth } from '../context/AuthContext'
import { useConfig } from '../context/ConfigContext'
import { loadPublishedCustomModules, deleteCustomModule, acceptCompletions, reissueModuleForAllCompleters } from '../lib/aiStudioService'
import { modules as builtInModules, SEED_COURSES as builtInCourses } from '../data/modules'
import { mergeCatalogue } from '../data/catalogue'
import { moduleContentChangedAt } from '../data/contentFreshness'

export default function AdminModulesPage() {
  const { user } = useAuth()
  // Deleting a module removes it from every trainee's catalogue, and reverts an
  // override for everyone — firestore.rules makes that admin-only, so the
  // buttons follow rather than being offered and refused.
  const isAdmin = user?.isAdmin === true
  const { allCourses } = useConfig()
  const [customModules, setCustomModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCourse, setSelectedCourse] = useState('all')
  const [selectedType, setSelectedType] = useState('all') // 'all' | 'builtin' | 'custom' | 'override'
  const [deletingId, setDeletingId] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [actionSuccess, setActionSuccess] = useState(null)

  useEffect(() => {
    fetchModules()
  }, [])

  async function fetchModules() {
    setLoading(true)
    try {
      const list = await loadPublishedCustomModules()
      setCustomModules(list)
    } catch (err) {
      console.error('Failed to load custom modules:', err)
      setActionError('Could not load custom modules from database.')
    } finally {
      setLoading(false)
    }
  }

  // Built-ins and Firestore modules as one list, an override replacing the
  // built-in it shares an id with. Shared with the Courses and Lessons pages so
  // the three cannot disagree about what the catalogue holds.
  const allCombinedModules = useMemo(
    () => mergeCatalogue(builtInModules, customModules),
    [customModules],
  )

  // Aggregate stats
  const stats = useMemo(() => {
    let builtinCount = 0
    let customCount = 0
    let overrideCount = 0
    for (const m of allCombinedModules) {
      if (m.isCustom) customCount++
      else builtinCount++
      if (m.hasOverride) overrideCount++
    }
    return {
      total: allCombinedModules.length,
      builtin: builtinCount,
      custom: customCount,
      override: overrideCount,
    }
  }, [allCombinedModules])

  // Track options for filtering
  const courseOptions = useMemo(() => {
    const courseSet = new Map()
    for (const t of allCourses || builtInCourses) {
      courseSet.set(t.id, t.name)
    }
    return Array.from(courseSet.entries()).map(([id, name]) => ({ id, name }))
  }, [allCourses])

  /**
   * Stop this edit asking previous completers to retake it.
   *
   * Only meaningful while the module HAS an unaccepted change date — hence the
   * button appearing only then. See contentFreshness.js for why this moves a
   * date rather than editing anybody's progress.
   */
  async function handleAcceptCompletions(mod) {
    if (!window.confirm(
      `Keep the existing completions for "${mod.title}"?\n\n` +
      'Anyone who finished it before the last edit will count as complete again, ' +
      'and will not be asked to retake it. Publishing the module again re-arms the request.'
    )) return
    setActionError(null)
    const res = await acceptCompletions(mod.id)
    if (res?.success) {
      setActionSuccess(`Existing completions of "${mod.title}" now count again.`)
      fetchModules()
    } else {
      setActionError(res?.error || 'That did not save.')
    }
  }

  /**
   * Ask everyone who completed this module to take it again.
   *
   * For BUILT-IN modules the freshness rule cannot help: their content is code,
   * so there is no publish date to compare a completion against. This is the
   * explicit version of the same request.
   */
  async function handleRetakeAll(mod) {
    if (!window.confirm(
      `Ask everyone who has completed "${mod.title}" to take it again?\n\n` +
      'Their previous score is kept and stays visible in the admin drill-down, ' +
      'but the module stops counting as complete until they retake it.'
    )) return
    setActionError(null)
    const res = await reissueModuleForAllCompleters(mod.id)
    if (res?.success) {
      setActionSuccess(res.count === 0
        ? `Nobody had completed "${mod.title}", so nothing changed.`
        : `Asked ${res.count} ${res.count === 1 ? 'trainee' : 'trainees'} to retake "${mod.title}".`)
    } else {
      setActionError(res?.error || 'That did not save.')
    }
  }

  async function handleDeleteOrResetModule(mod) {
    if (mod.hasOverride) {
      const confirmMsg = `Reset "${mod.title}" to default built-in version?\n\nThis will remove the customized Firestore version and immediately restore the original standard module for all learners.`
      if (!window.confirm(confirmMsg)) return

      setDeletingId(mod.id)
      setActionError(null)
      setActionSuccess(null)

      const res = await deleteCustomModule(mod.id)
      setDeletingId(null)

      if (res.success) {
        setCustomModules((prev) => prev.filter((m) => String(m.id) !== String(mod.id)))
        setActionSuccess(`Reset "${mod.title}" to original built-in default.`)
        setTimeout(() => setActionSuccess(null), 4000)
      } else {
        setActionError(`Failed to reset module: ${res.error || 'Permission denied'}`)
      }
    } else if (mod.isCustom) {
      const confirmMsg = `Are you sure you want to delete custom module "${mod.title}"?\n\nThis will permanently remove the module from the catalog for all trainees. Completed progress records for past trainees will remain safely in the database.`
      if (!window.confirm(confirmMsg)) return

      setDeletingId(mod.id)
      setActionError(null)
      setActionSuccess(null)

      const res = await deleteCustomModule(mod.id)
      setDeletingId(null)

      if (res.success) {
        setCustomModules((prev) => prev.filter((m) => String(m.id) !== String(mod.id)))
        setActionSuccess(`Deleted module "${mod.title}" successfully.`)
        setTimeout(() => setActionSuccess(null), 4000)
      } else {
        setActionError(`Failed to delete module: ${res.error || 'Permission denied'}`)
      }
    }
  }

  const filteredModules = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    return allCombinedModules.filter((m) => {
      // Type filter
      if (selectedType === 'builtin' && (!m.isBuiltIn || m.hasOverride)) return false
      if (selectedType === 'custom' && !m.isCustom) return false
      if (selectedType === 'override' && !m.hasOverride) return false

      // Track filter
      if (selectedCourse !== 'all' && m.track !== selectedCourse) return false

      // Search query
      if (q) {
        const matchesTitle = (m.title || '').toLowerCase().includes(q)
        const matchesSubtitle = (m.subtitle || '').toLowerCase().includes(q)
        const matchesDesc = (m.description || '').toLowerCase().includes(q)
        const matchesCourse = (m.track || '').toLowerCase().includes(q)
        const matchesPhase = (m.phase || '').toLowerCase().includes(q)
        const matchesId = (m.id || '').toLowerCase().includes(q)
        const matchesAuthor = (m.publishedBy?.displayName || m.publishedBy?.email || '').toLowerCase().includes(q)
        if (!matchesTitle && !matchesSubtitle && !matchesDesc && !matchesCourse && !matchesPhase && !matchesId && !matchesAuthor) {
          return false
        }
      }

      return true
    })
  }, [allCombinedModules, selectedType, selectedCourse, searchQuery])

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <AdminNav />

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 shrink-0">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training Modules</h1>
            <p className="mt-1 text-sm text-gray-500">
              Browse, preview, and edit all training modules. Click <strong>Edit in AI Studio</strong> on any module to customize its content, add practice sheets, or refine quiz questions.
            </p>
          </div>
        </div>

        <Link
          to="/admin/studio"
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:from-indigo-500 hover:to-purple-500 transition-all hover:scale-102 shrink-0"
        >
          <Plus className="h-4 w-4" />
          Create in AI Studio
        </Link>
      </div>


      {/* Action alerts */}
      {actionSuccess && (
        <div className="mb-6 flex items-center gap-2 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800 border border-emerald-200">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <span>{actionSuccess}</span>
        </div>
      )}

      {actionError && (
        <div className="mb-6 flex items-center gap-2 rounded-xl bg-red-50 p-4 text-sm text-red-800 border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      {/* Filters & Search Row */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, track, description, or creator..."
            className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none shadow-2xs"
          />
        </div>

        {/* Dropdowns */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Track Filter */}
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 focus:border-indigo-500 focus:outline-none shadow-2xs"
          >
            <option value="all">All Tracks / Courses</option>
            {courseOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 focus:border-indigo-500 focus:outline-none shadow-2xs"
          >
            <option value="all">All Types ({allCombinedModules.length})</option>
            <option value="builtin">Core Standard ({stats.builtin})</option>
            <option value="custom">Custom Created ({stats.custom})</option>
            {stats.override > 0 && <option value="override">Live Overrides ({stats.override})</option>}
          </select>
        </div>
      </div>

      {/* Modules List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        </div>
      ) : filteredModules.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
          No modules match your current filters.
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredModules.map((mod) => {
            const courseObj = (allCourses || builtInCourses).find((t) => t.id === mod.track)
            const pagesCount = Array.isArray(mod.pages) ? mod.pages.length : (mod.isBuiltIn && !mod.hasOverride ? 1 : 0)
            const quizCount = Array.isArray(mod.quiz?.questions) ? mod.quiz.questions.length : (mod.isBuiltIn && !mod.hasOverride ? 'Interactive' : 0)
            const isDeleting = deletingId === mod.id

            return (
              <div
                key={mod.id}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs transition-shadow hover:shadow-md"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-2 flex-1">
                    {/* Badges */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 border border-indigo-200/60">
                        {courseObj?.name || mod.track || 'General'}
                      </span>

                      {mod.phase && (
                        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {mod.phase}
                        </span>
                      )}

                      {mod.hasOverride ? (
                        <span className="inline-flex items-center gap-1 rounded-md bg-purple-50 px-2 py-0.5 text-xs font-semibold text-purple-700 border border-purple-200/60">
                          <Sparkles className="h-3 w-3" /> Live Override
                        </span>
                      ) : mod.isCustom ? (
                        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 border border-emerald-200/60">
                          <CheckCircle2 className="h-3 w-3" /> Live
                        </span>
                      ) : (
                        <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          Core Module
                        </span>
                      )}

                      <span className="text-xs text-gray-400 font-mono">ID: {mod.id}</span>
                    </div>

                    {/* Titles */}
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{mod.title}</h2>
                      {mod.subtitle && <p className="text-xs font-medium text-indigo-600">{mod.subtitle}</p>}
                    </div>

                    {mod.description && <p className="text-sm text-gray-600 line-clamp-2">{mod.description}</p>}

                    {/* Metadata Footer */}
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-100">
                      {pagesCount !== undefined && (
                        <span className="inline-flex items-center gap-1">
                          <BookOpen className="h-3.5 w-3.5 text-gray-400" />
                          {typeof pagesCount === 'number' ? `${pagesCount} page${pagesCount !== 1 ? 's' : ''}` : pagesCount}
                        </span>
                      )}
                      {quizCount !== undefined && (
                        <span className="inline-flex items-center gap-1">
                          <HelpCircle className="h-3.5 w-3.5 text-gray-400" />
                          {typeof quizCount === 'number' ? `${quizCount} quiz question${quizCount !== 1 ? 's' : ''}` : `${quizCount} Quiz`}
                        </span>
                      )}
                      {mod.estimatedMinutes && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          ~{mod.estimatedMinutes} mins
                        </span>
                      )}
                      {mod.publishedBy?.displayName && (
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3.5 w-3.5 text-gray-400" />
                          Published by {mod.publishedBy.displayName}
                        </span>
                      )}
                      {mod.publishedAt && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-gray-400" />
                          {new Date(mod.publishedAt).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap sm:flex-col items-center sm:items-end gap-2 shrink-0">
                    <Link
                      to={`/module/${mod.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-2xs"
                    >
                      <Eye className="h-3.5 w-3.5 text-gray-500" />
                      Preview
                      <ExternalLink className="h-3 w-3 text-gray-400" />
                    </Link>

                    <Link
                      to={`/admin/studio/${mod.id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors shadow-2xs"
                      title="Open and edit this module in AI Module Studio"
                    >
                      <Edit3 className="h-3.5 w-3.5 text-indigo-600" />
                      Edit in AI Studio
                    </Link>

                    {isAdmin && (
                      <button
                        onClick={() => handleRetakeAll(mod)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50/70 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 transition-colors shadow-2xs"
                        title="Ask every trainee who has completed this module to take it again"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Ask all to retake
                      </button>
                    )}

                    {isAdmin && mod.isCustom && moduleContentChangedAt(mod) > 0 && (
                      <button
                        onClick={() => handleAcceptCompletions(mod)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors shadow-2xs"
                        title="This edit was minor — keep the existing completions instead of asking people to retake"
                      >
                        <Check className="h-3.5 w-3.5" />
                        Keep existing completions
                      </button>
                    )}

                    {isAdmin && mod.hasOverride ? (
                      <button
                        onClick={() => handleDeleteOrResetModule(mod)}
                        disabled={isDeleting}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50/60 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100 transition-colors disabled:opacity-50"
                        title="Revert back to original built-in module"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        {isDeleting ? 'Resetting...' : 'Reset to Default'}
                      </button>
                    ) : isAdmin && mod.isCustom ? (
                      <button
                        onClick={() => handleDeleteOrResetModule(mod)}
                        disabled={isDeleting}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50/50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100/80 transition-colors disabled:opacity-50"
                        title="Permanently remove this custom module"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {isDeleting ? 'Deleting...' : 'Delete Module'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
